import { useLocalStorage } from "@/composables/useLocalStorage";
import { defineStore } from "pinia";
import { computed, ref } from "vue";

export type RecentlyVisitedType = "degree" | "news" | "page";

export type RecentlyVisitedItem = {
  slug: string;
  url: string;
  title: string;
  metaDescription?: string;
  image?: string;
  type: RecentlyVisitedType;
  visitedAt: string;
};

export type RecentlyVisitedInput = Omit<RecentlyVisitedItem, "visitedAt"> & {
  visitedAt?: string;
};

const RECENTLY_VISITED_STORAGE_KEY = "recently-visited";
const MAX_RECENTLY_VISITED_ITEMS = 6;

export const useRecentlyVisitedStore = defineStore("recentlyVisited", () => {
  const items = ref<RecentlyVisitedItem[]>([]);
  const storeLoading = ref<boolean>(true);

  const hasItems = computed(() => items.value.length > 0);

  const saveItems = async () => {
    if (!import.meta.client) {
      return;
    }

    const localStorage = useLocalStorage();
    await localStorage.setItem(RECENTLY_VISITED_STORAGE_KEY, items.value);
  };

  const addVisit = async (visit: RecentlyVisitedInput) => {
    const nextVisit: RecentlyVisitedItem = {
      ...visit,
      visitedAt: visit.visitedAt || new Date().toISOString()
    };

    items.value = [nextVisit, ...items.value.filter((item) => item.slug !== nextVisit.slug)].slice(
      0,
      MAX_RECENTLY_VISITED_ITEMS
    );

    await saveItems();

    return nextVisit;
  };

  const removeVisit = async (slug: string) => {
    items.value = items.value.filter((item) => item.slug !== slug);
    await saveItems();
  };

  const clearVisits = async () => {
    items.value = [];

    if (!import.meta.client) {
      return;
    }

    const localStorage = useLocalStorage();
    await localStorage.removeItem(RECENTLY_VISITED_STORAGE_KEY);
  };

  const initState = async () => {
    if (!import.meta.client) {
      storeLoading.value = false;
      return;
    }

    const localStorage = useLocalStorage();
    const storedItems = await localStorage.getItem<RecentlyVisitedItem[]>(
      RECENTLY_VISITED_STORAGE_KEY
    );

    items.value = Array.isArray(storedItems)
      ? storedItems.slice(0, MAX_RECENTLY_VISITED_ITEMS)
      : [];
    storeLoading.value = false;
  };

  return {
    items,
    storeLoading,
    hasItems,
    addVisit,
    removeVisit,
    clearVisits,
    initState
  };
});
